import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material"
import { Fragment, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { FormProvider, useForm } from "react-hook-form"
import { BASEGRAPH_FORM_FIELDS } from "./const"
import { FormBuilder } from "@components/FormBuilder"
import {
  graphsApi,
  useDeleteGraphMutation,
  useLazyGetGraphQuery,
  useUpdateGraphMutation,
} from "@store/services/graphsService"
import DeleteDialog from "@components/DeleteDialog"
import { UpdateGraphDto } from "@custom-types/graphs"
import SuccessDialog from "@components/SuccessDialog"
import { format, isValid, parseISO } from "date-fns"
import { useAppDispatch } from "@store/hooks"
import ScenariosTable from "@features/administration/products/BaseGraph/BaseGraphEdit/ScenariosTable"

export const BaseGraphEdit = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const formMethods = useForm({
    defaultValues: {
      key: "",
      description: "",
      product_key: "",
      product_organisation_key: "",
      product_division_key: "",
      base_graph_family_key: "",
      creator_email: "",
      s3_location: "",
      s3_hash: "",
      version: "",
      owner_group_key: "",
      published: false,
      endDate: "",
      startDate: "",
      start_date: "",
      end_date: "",
      rolling_duration: "",
      last_updated: "",
      sampler_version: "0",
      yearly_rolling_start_month: 1,
      simulation_frequency: "yearly",
      scenario_version: "",
      scenario_source: "",
    },
  })
  const { watch, handleSubmit } = formMethods
  const { key } = useParams()
  const [organisationKey, divisionKey, productKey] = (key ?? "").split("#")
  const endDate = watch("endDate")
  const startDate = watch("startDate")
  const start_date = watch("start_date")
  const end_date = watch("end_date")
  const rollingDuration = watch("rolling_duration")
  const simulationFrequency = watch("simulation_frequency")
  const [fetchGraph, { data: graphData, isLoading: isGetLoading }] = useLazyGetGraphQuery()
  const [updateGraph, { isSuccess: isUpdateSuccess, isLoading: isUpdateLoading }] =
    useUpdateGraphMutation()
  const [deleteGraph, { isSuccess: isDeleteSuccess, isLoading: isDeleteLoading }] =
    useDeleteGraphMutation()
  const [successMessage, setSuccessMessage] = useState("")

  const onCancelClickHandler = () => {
    dispatch(graphsApi.util.resetApiState())
    navigate(`/Administration/Products/${productKey}/${organisationKey}/${divisionKey}`)
  }

  const onDeleteClickHandler = () => {
    setIsDeleteOpen(false)
    graphData && deleteGraph(graphData.key)
  }

  const onSubmit = (data: UpdateGraphDto) =>
    graphData &&
    updateGraph({
      key: graphData.key,
      body: {
        ...data,
        start_date:
          data.startDate === "rolling"
            ? "rolling"
            : format(parseISO(data.start_date), "yyyy-MM-dd"),
        end_date:
          data.endDate === "rolling" ? "rolling" : format(parseISO(data.end_date), "yyyy-MM-dd"),
      },
    })

  useEffect(() => {
    if (key) {
      fetchGraph(key)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [key])

  useEffect(() => {
    if (graphData) {
      // @ts-expect-error TODO: GraphDto does not fit to default values that are determining type of form values
      formMethods.reset(graphData)
    }
  }, [graphData, formMethods])

  useEffect(() => {
    if (isDeleteSuccess) {
      setSuccessMessage("Basegraph deleted successfully.")
    }
  }, [isDeleteSuccess])

  useEffect(() => {
    if (isUpdateSuccess) {
      setSuccessMessage("Basegraph saved successfully.")
    }
  }, [isUpdateSuccess])

  useEffect(() => {
    if (startDate === "fixed" && endDate === "fixed" && !rollingDuration) {
      formMethods.setValue("rolling_duration", "calculated")
    }
  }, [startDate, endDate, rollingDuration, formMethods])

  useEffect(() => {
    if (isValid(parseISO(start_date))) {
      formMethods.setValue("startDate", "fixed")
    } else {
      formMethods.setValue("startDate", "rolling")
    }
  }, [start_date, formMethods])

  useEffect(() => {
    if (isValid(parseISO(end_date))) {
      formMethods.setValue("endDate", "fixed")
    } else {
      formMethods.setValue("endDate", "rolling")
    }
  }, [end_date, formMethods])

  if (isGetLoading || isUpdateLoading || isDeleteLoading) {
    return (
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    )
  }

  const scenarios = graphData?.scenarios ?? []

  return (
    <>
      <FormProvider {...formMethods}>
        <form autoComplete={"off"} onSubmit={handleSubmit(onSubmit)}>
          <Grid container item gap={6} direction="column" sx={{ p: 1 }}>
            <Grid item>
              <Typography variant="h4" sx={{ mb: 4 }}>
                Edit Basegraph - {key?.split("#").slice(-1)}
              </Typography>
            </Grid>

            <Grid item container>
              <Grid item container gap={1} sx={{ maxWidth: "600px" }}>
                {Object.entries(BASEGRAPH_FORM_FIELDS).map(([fieldName, fieldValue]) => (
                  <Fragment key={fieldName}>
                    {endDate !== "fixed" && fieldName === "end_date" ? null : startDate !==
                        "fixed" && fieldName === "start_date" ? null : simulationFrequency !==
                        "yearly" && fieldName === "yearly_rolling_start_month" ? null : (
                      <Grid container item>
                        <FormBuilder field={fieldName} component={fieldValue} />
                      </Grid>
                    )}
                  </Fragment>
                ))}
              </Grid>
              {scenarios.length > 0 && (
                <Grid item>
                  <ScenariosTable scenarios={scenarios} />
                </Grid>
              )}
            </Grid>
            <Grid item container gap={1}>
              <Grid item>
                <Button variant="contained" type="submit">
                  Update
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color={"error"}
                  type="button"
                  onClick={onCancelClickHandler}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color={"error"}
                  type="button"
                  onClick={() => setIsDeleteOpen(true)}
                >
                  Delete
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </FormProvider>

      <DeleteDialog
        open={isDeleteOpen}
        setOpen={setIsDeleteOpen}
        onDeleteClick={onDeleteClickHandler}
      />
      <SuccessDialog
        open={!!successMessage}
        onCloseClick={onCancelClickHandler}
        successMessage={successMessage}
      />
    </>
  )
}
